import { useMemo, useRef, useState } from "react";
import { Icon } from "@/components/common/Icon";
import { focusRingOff, focusRingOn, INPUT_STYLE } from "@/lib/form-styles";
import { chipToFocusAfterRemove, filterUserCandidates, type UserListUser } from "@/lib/user-list";

export type { UserListUser };

interface UserListProps {
	/** 追加候補となるユーザー (組織の全ユーザーなど)。 */
	users: UserListUser[];
	/** 選択中のユーザー ID (表示順)。 */
	selectedIds: string[];
	onChange: (ids: string[]) => void;
	disabled?: boolean;
}

const CHIP_STYLE: React.CSSProperties = {
	display: "inline-flex",
	alignItems: "center",
	gap: 2,
	maxWidth: "100%",
	padding: "3px 4px 3px 10px",
	borderRadius: "var(--radius-full)",
	background: "var(--surface-2)",
	color: "var(--fg-1)",
	fontSize: "var(--text-caption)",
	fontFamily: "var(--font-sans)",
	outline: "none",
	cursor: "default",
	transition: "box-shadow 150ms ease",
};

/**
 * ユーザーを検索して追加・削除するコンパクトなチップリスト。
 * チップはクリックでフォーカスでき、フォーカス中に Delete / Backspace で削除する。
 */
export function UserList({ users, selectedIds, onChange, disabled }: UserListProps) {
	const [query, setQuery] = useState("");
	const [highlight, setHighlight] = useState(0);
	const inputRef = useRef<HTMLInputElement>(null);
	const chipRefs = useRef(new Map<string, HTMLSpanElement>());

	const usersById = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
	const candidates = useMemo(
		() => filterUserCandidates(users, selectedIds, query),
		[users, selectedIds, query],
	);
	const highlightIndex = Math.min(highlight, Math.max(candidates.length - 1, 0));

	function add(id: string) {
		onChange([...selectedIds, id]);
		setQuery("");
		setHighlight(0);
		inputRef.current?.focus();
	}

	function remove(id: string, refocus: boolean) {
		const next = refocus ? chipToFocusAfterRemove(selectedIds, id) : null;
		onChange(selectedIds.filter((selected) => selected !== id));
		if (!refocus) return;
		// onChange 後の再レンダーを待たずに残るチップの DOM へフォーカスを移す
		// (削除対象が消えてフォーカスが body に落ちるのを防ぐ)。
		if (next !== null) chipRefs.current.get(next)?.focus();
		else inputRef.current?.focus();
	}

	function handleChipKeyDown(e: React.KeyboardEvent, id: string) {
		if (e.key === "Delete" || e.key === "Backspace") {
			e.preventDefault();
			remove(id, true);
		}
	}

	function handleInputKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
		if (e.key === "ArrowDown" && candidates.length > 0) {
			e.preventDefault();
			setHighlight((highlightIndex + 1) % candidates.length);
		} else if (e.key === "ArrowUp" && candidates.length > 0) {
			e.preventDefault();
			setHighlight((highlightIndex - 1 + candidates.length) % candidates.length);
		} else if (e.key === "Enter" && query.trim() !== "") {
			// 検索中の Enter はダイアログのフォーム送信ではなく候補の追加。
			e.preventDefault();
			if (candidates.length > 0) add(candidates[highlightIndex].id);
		} else if (e.key === "Escape" && query !== "") {
			// 検索語のクリアに使う。ダイアログを閉じる Escape とは区別し、
			// <dialog> のネイティブ close (preventDefault で抑止) も走らせない。
			e.preventDefault();
			e.stopPropagation();
			setQuery("");
		}
	}

	return (
		<div data-testid="user-list" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
			{selectedIds.length > 0 && (
				<div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
					{selectedIds.map((id) => {
						const user = usersById.get(id);
						return (
							<span
								key={id}
								tabIndex={disabled ? undefined : 0}
								ref={(el) => {
									if (el) chipRefs.current.set(id, el);
									else chipRefs.current.delete(id);
								}}
								data-testid={`user-chip-${id}`}
								style={CHIP_STYLE}
								onClick={(e) => e.currentTarget.focus()}
								onKeyDown={disabled ? undefined : (e) => handleChipKeyDown(e, id)}
								onFocus={(e) => focusRingOn(e.currentTarget)}
								onBlur={(e) => {
									e.currentTarget.style.boxShadow = "none";
								}}
								title={user?.email}
							>
								<span
									style={{
										overflow: "hidden",
										textOverflow: "ellipsis",
										whiteSpace: "nowrap",
									}}
								>
									{user?.name ?? id}
								</span>
								{!disabled && (
									<button
										type="button"
										data-testid={`user-chip-remove-${id}`}
										onClick={() => remove(id, false)}
										style={{
											display: "inline-flex",
											alignItems: "center",
											justifyContent: "center",
											width: 18,
											height: 18,
											padding: 0,
											border: 0,
											borderRadius: "var(--radius-full)",
											background: "transparent",
											color: "var(--fg-2)",
											cursor: "pointer",
										}}
										onMouseEnter={(e) => {
											e.currentTarget.style.color = "var(--fg-1)";
										}}
										onMouseLeave={(e) => {
											e.currentTarget.style.color = "var(--fg-2)";
										}}
									>
										<Icon name="x" size={12} />
									</button>
								)}
							</span>
						);
					})}
				</div>
			)}
			{!disabled && (
				<div style={{ position: "relative" }}>
					<input
						ref={inputRef}
						value={query}
						onChange={(e) => {
							setQuery(e.target.value);
							setHighlight(0);
						}}
						onKeyDown={handleInputKeyDown}
						placeholder="名前かメールアドレスで検索して追加"
						autoComplete="off"
						spellCheck={false}
						data-testid="user-list-search"
						style={{ ...INPUT_STYLE, height: 32, fontSize: "var(--text-caption)" }}
						onFocus={(e) => focusRingOn(e.currentTarget)}
						onBlur={(e) => focusRingOff(e.currentTarget)}
					/>
					{candidates.length > 0 && (
						<div
							data-testid="user-list-candidates"
							style={{
								position: "absolute",
								top: "calc(100% + 4px)",
								left: 0,
								right: 0,
								zIndex: 10,
								display: "flex",
								flexDirection: "column",
								padding: 4,
								borderRadius: "var(--radius-3)",
								background: "var(--surface-1)",
								boxShadow: "var(--shadow-ring), var(--shadow-card)",
								maxHeight: 240,
								overflowY: "auto",
							}}
						>
							{candidates.map((user, index) => (
								<button
									key={user.id}
									type="button"
									data-testid={`user-list-candidate-${user.id}`}
									// クリックより先に input の blur が走ってもドロップダウンが閉じないよう
									// mousedown で確定する。
									onMouseDown={(e) => {
										e.preventDefault();
										add(user.id);
									}}
									onMouseEnter={() => setHighlight(index)}
									style={{
										display: "flex",
										alignItems: "baseline",
										gap: 8,
										padding: "6px 8px",
										border: 0,
										borderRadius: "var(--radius-2)",
										background: index === highlightIndex ? "var(--menu-hover-bg)" : "transparent",
										color: index === highlightIndex ? "var(--fg-0)" : "var(--fg-1)",
										fontSize: "var(--text-caption)",
										fontFamily: "var(--font-sans)",
										textAlign: "left",
										cursor: "pointer",
									}}
								>
									<span style={{ whiteSpace: "nowrap" }}>{user.name}</span>
									<span
										style={{
											color: "var(--fg-2)",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
										}}
									>
										{user.email}
									</span>
								</button>
							))}
						</div>
					)}
				</div>
			)}
		</div>
	);
}
